// Teams tab: browse/filter every FBS team with stat columns, sourced from
// the AuthContext teams map (not a fresh teams query). Roster viewing/
// cutting lives on the Lineup tab (TeamSlot.js already has swipe-to-cut,
// captain/5x, drag), so this tab is purely acquisition: browse, sort, and
// pick up a team.
//
// Unrostered teams get an action button that depends on the season phase:
// once a real lineup week exists, it's "Bid" (waiver bidding, migration
// 037 / place_bid). Before that, place_bid hard-errors ("Season has not
// started") because the weekly-budget/Tuesday-resolution mechanism has no
// meaning yet, so preseason gets "Add" instead: instant, unconditional,
// first-come-first-served (migration 039 / instant_add_team). The RPC
// itself refuses once a real week exists, so the two paths stay in sync by
// construction rather than by a shared flag.
//
// Rows are a dense list, not tiles: one line for identity, one for matchup,
// two numeric columns. A phone fits ~8 teams per screen that way, and
// browsing 130 teams is the whole job of this tab.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Plus, TrendingUp, Star, ChevronRight, Lock } from "lucide-react";
import { useLeague } from "../context/LeagueContext";
import { useDraftContext } from "../context/DraftContext";
import { useTeams } from "../hooks/useTeams";
import { useSeasonConfig } from "../hooks/useSeasonConfig";
import { useWeeklyLineup } from "../hooks/useWeeklyLineup";
import { supabase } from "../supabase/supabase";
import { teamLogoUrl } from "../utils/teamLogo";
import { calculateAverage } from "../utils/teamStats";
import { rankTrending, popularPickups } from "../utils/trending";
import { getCached, setCached, IDLE_TTL_MS } from "../utils/queryCache";
import { fetchWeekGames } from "../utils/weekGames";
import { normalizeTeamName } from "../utils/teamName";
import { useBiddingWindow } from "../hooks/useBiddingWindow";
import { useWatchlist } from "../hooks/useWatchlist";
import {
  FIRST_SCORING_WEEK,
  isInstantAddOpen,
  isWeeklyFreeAgencyOpen,
} from "../utils/season";
import { parseRecord } from "../utils/teamStats";
import { buildGamesBySlug, getGameSpreadDisplay, toJSDate, isGameLive, isTeamLocked } from "../utils/gameStatus";
import TeamLogoImage from "../components/league/TeamLogoImage";
import TeamDrawer from "../components/league/TeamDrawer";
import BidSheet from "../components/teams/BidSheet";
import InstantAddSheet from "../components/teams/InstantAddSheet";
import { usePageTour } from "../hooks/usePageTour";
import HelpMark from "../components/HelpMark";
import TourAnchor from "../tours/TourAnchor";

// Width of the row's leading slot: a 28px add button, a 4px gap and a 28px
// watchlist star. The list header and loading rows reserve the same width, so
// change them together.
const ACTION_SLOT_WIDTH = 60;

// Only the current week's games are loaded, so a weekday is unambiguous and
// costs a third of what "Sep 5, 12:30 PM" does. This is what buys the
// matchup line enough room for the opponent.
// Weekday, calendar date, then time: "Sat Sep 5 · 8:00 PM". The weekday
// alone answers "is this the early game or the late one" but not "is this
// game this week or next", and the schedule runs far enough ahead on this
// tab that the second question is the one being asked.
const formatKickoff = (value) => {
  const date = toJSDate(value);
  if (!date) return null;
  const day = date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time}`;
};

// Team colour helpers for the trending cards. teams.color arrives from the
// DB sometimes with a leading "#" and sometimes without, and a handful of
// schools have a near-white primary that would leave a white logo invisible
// on it, so those get darkened rather than used raw.
const normalizeHex = (value) => {
  if (!value) return null;
  const hex = value.startsWith("#") ? value : `#${value}`;
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex : null;
};

const hexChannels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const shade = (hex, factor) =>
  `#${hexChannels(hex)
    .map((v) => Math.round(Math.min(255, Math.max(0, v * factor))).toString(16).padStart(2, "0"))
    .join("")}`;

// WCAG relative luminance, used only to detect the near-white primaries.
const luminance = (hex) => {
  const [r, g, b] = hexChannels(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// The trending card's top panel: the school's primary as a solid block, its
// abbreviation set large on it, and a stripe of the alternate colour beneath.
// It replaced an oversized, 30% opacity helmet: /public/logos are generic
// helmet stickers on a white, non-transparent square, so scaling one up only
// ever produced a blurry helmet inside a faint white box.
//
// Text colour is chosen by contrast, not fixed to white: a gold or pale
// primary with white type on it is unreadable. The stripe falls back to a
// darker shade of the panel when the alternate is missing, near-white (it
// would vanish against the card's white body), or too close to the primary to
// read as a separate band.
// Literal hex, deliberately, and the same below for the white it is weighed
// against. These are arguments to luminance() and contrast(), not style
// values: a var() string cannot be parsed into channels, so tokenising them
// silently broke the choice and put dark ink on a dark primary.
//
// They are also theme-independent by nature. The panel is filled with the
// school's own colour, which does not change between light and dark, so the
// ink that reads on it does not either.
const DARK_INK = "#1C1C1E";
const PANEL_LIGHT_INK = "#FFFFFF";
const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};
const trendingPanel = (team) => {
  const base = normalizeHex(team?.color) || "#8E8E93";
  const bg = luminance(base) > 0.85 ? shade(base, 0.65) : base;
  const fg = contrast(bg, PANEL_LIGHT_INK) >= contrast(bg, DARK_INK) ? PANEL_LIGHT_INK : DARK_INK;
  const alt = normalizeHex(team?.alternate_color);
  const stripe = alt && luminance(alt) < 0.85 && contrast(alt, bg) >= 1.5 ? alt : shade(bg, 0.7);
  return { bg, fg, stripe };
};

// Opponent short form, in preference order:
//   1. teams.abbreviation ("MICH"). Populated by whatever seeded the teams
//      table, not by our importers, so coverage is unknown and it cannot be
//      relied on alone.
//   2. The school name with "State" contracted, which is what makes most of
//      the long ones fit ("Ohio State" -> "Ohio St").
//   3. A hard truncation, as a last resort.
// Never the last word alone: that turns "Ohio State" into "State", which
// reads as a different team entirely.
const OPPONENT_MAX = 13;
const shortOpponent = (slug, allTeams) => {
  const team = allTeams?.[slug];
  const abbreviation = team?.abbreviation;
  if (abbreviation && abbreviation.length <= 5) return abbreviation.toUpperCase();

  const name = team?.school;
  if (!name) return slug || "";
  const compact = name.replace(/\bState\b/g, "St").replace(/\bUniversity\b/g, "").trim();
  return compact.length > OPPONENT_MAX ? `${compact.slice(0, OPPONENT_MAX - 1)}…` : compact;
};

// The spread, set apart from the rest of the matchup line rather than
// trailing it. As the tail of a sentence it was the first thing truncated on
// a narrow row, and it is the number that says whether a win was ever likely.
// Negative means favoured, matching scoring.js.
function SpreadChip({ value }) {
  if (!value) return null;
  const tone = value === "PICK"
    ? { backgroundColor: "var(--color-bg-subtle)", color: "var(--color-text-muted)" }
    : value.startsWith("-")
      ? { backgroundColor: "var(--color-band-bg)", color: "var(--color-brand)" }
      : { backgroundColor: "var(--color-captain-fill)", color: "var(--color-captain-text)" };
  return (
    <span className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold" style={tone}>
      {value}
    </span>
  );
}

// One control, rendered above each section. Free agents and Owned answer
// different questions, so they sort independently, but the options have to
// stay identical or the same word would mean two things on one screen.
function SortSelect({ value, onChange, label }) {
  return (
    <div className="flex justify-end mb-2 px-4 sm:px-0">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="text-xs font-semibold rounded-lg px-3 py-1.5 outline-none cursor-pointer"
        style={{ backgroundColor: "var(--color-bg-card)", color: "var(--color-text-strong)", border: "1px solid var(--color-border)" }}
      >
        <option value="points">Sort: Points</option>
        <option value="spread">Sort: Expected margin</option>
        <option value="record">Sort: Record</option>
        <option value="az">Sort: A-Z</option>
      </select>
    </div>
  );
}

function TeamsTab() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { currentMemberId, currentWeek, members: ctxMembers, leagueData, isDemo } = useLeague();
  usePageTour("teams", { demo: isDemo, nextLabel: "Sign up now", nextTo: "/signup", nextEnds: true });
  const isTestLeague = Boolean(leagueData?.is_test);
  const { draft } = useDraftContext();
  const { teams: allTeams } = useTeams();
  const { config: seasonConfig } = useSeasonConfig();
  const faLocked = seasonConfig?.faLocked || false;

  // Bidding runs from 10am Sunday, when the week advances, to 4am Tuesday
  // Eastern. useBiddingWindow reads the week from
  // seasonConfig, not useLeague().currentWeek, which floors every non-numeric
  // label to 1 and would read the preseason as week 1.
  const biddingWindow = useBiddingWindow();
  const biddingOpen = biddingWindow.state === "open";

  // The instant add board closes at the first kickoff of week 0, not when the
  // week label flips on the Tuesday before it. A league that drafts after
  // that keeps the board for a day, since it never had a preseason window and
  // waivers do not open until FIRST_FREE_AGENCY_WEEK.
  // Keyed by slug, from `games` rather than team_season_stats: that table only
  // carries the week it happens to be processing, so it is empty for every
  // team in the preseason and the whole tab read "No game scheduled".
  // Scoreboard and the Lineup tab already source it this way.
  //
  // Declared here rather than beside the fetch below because the add window
  // reads it, and a const used above its declaration is a runtime ReferenceError
  // that no build step catches.
  const [gamesBySlug, setGamesBySlug] = useState({});

  const instantAddOpen = isInstantAddOpen({
    seasonConfig,
    draftCompletedAt: draft?.completed_at,
    isTestLeague,
  });
  const weeklyFreeAgencyOpen = isWeeklyFreeAgencyOpen({ seasonConfig, isTestLeague });
  // Never in the public demo league, which a visitor can look at but not play.
  const canAcquire = !isDemo && (instantAddOpen || biddingOpen);

  // The board no longer shuts for everyone at the week's first kickoff; each
  // team closes at its own lineup lock (migration 076). Only the instant add
  // path cares: a bid placed Sunday or Monday resolves into next week, whose
  // games have not started.
  const lockedForAdd = (slug) => instantAddOpen && isTeamLocked(gamesBySlug[slug], slug).locked;

  // One short state, not a sentence: the detail is on the rules page. Title
  // Case, matching the rest of this notice family (the empty-claims text on
  // the Lineup tab's Waiver claims sheet, and BidSheet's closed-window text).
  //
  // Free agency and waivers now alternate with no gap: waivers from 10am
  // Sunday to 4am Tuesday, results at 6am, free agency from then until the
  // next Sunday. "Free Agency Closed" is left for faLocked, the off-season
  // and the weeks either side of the waiver season.
  const noticeText = (() => {
    // The demo league is is_test, so its windows read as always open. A
    // visitor cannot act on that, so say what they can do instead.
    if (isDemo) return "Demo League. Sign Up to Add Teams.";
    if (faLocked) return "Free Agency Closed";
    if (weeklyFreeAgencyOpen) return "Waivers Concluded. Free Agency Open.";
    if (instantAddOpen) return "Free Agency Open";
    switch (biddingWindow.state) {
      case "open":
        return "Waivers Open";
      case "resolving":
        return "Waivers Closed. Results At 6am ET.";
      default:
        return "Free Agency Closed";
    }
  })();

  // Only the "bidding is open" state has somewhere useful to send a manager:
  // their own claims. The others are either informational (paused, resolving,
  // closed) or already actionable right here on the board (instant add).
  const noticeLinksToWaivers = !isDemo && !faLocked && !instantAddOpen && biddingWindow.state === "open";

  const { weeklyLineups } = useWeeklyLineup({
    leagueId, memberId: currentMemberId, currentWeek,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [activeConference, setActiveConference] = useState("All");
  // Narrows the board to the manager's shortlist. Kept separate from the
  // conference chips rather than added to that list: it is a different
  // question, and both can sensibly be on at once (the watchlist, SEC only).
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  // Same store the drawer's star button writes to, so starring a team there
  // updates this chip's count without either component refetching.
  const watchlist = useWatchlist(currentMemberId, leagueId);
  // Raised by a row's star when the list is full. Shown under the filter
  // chips, since a row has no room for a sentence.
  const [watchError, setWatchError] = useState("");
  const toggleWatch = async (slug) => {
    setWatchError("");
    const reason = await watchlist.toggle(slug);
    if (reason) setWatchError(reason);
  };
  // Emptying the watchlist from the drawer hides the chip, which would
  // otherwise leave the filter stuck on with no control left to turn it off
  // and an empty board behind it.
  useEffect(() => {
    if (watchlistOnly && watchlist.slugs && watchlist.count === 0) setWatchlistOnly(false);
  }, [watchlistOnly, watchlist.slugs, watchlist.count]);
  // One key per section. They were a single piece of state, so changing the
  // sort above Free agents silently reordered Owned as well: two lists that
  // are read for different reasons (who can I take, who has what) were forced
  // to answer the same question.
  const [faSort, setFaSort] = useState("points");     // 'points' | 'spread' | 'record' | 'az'
  const [ownedSort, setOwnedSort] = useState("points");
  const [drawerTeam, setDrawerTeam] = useState(null);
  const [bidTarget, setBidTarget] = useState(null); // team slug, or null

  // League-wide ownership, sourced from weekly_lineups (league-scoped; not
  // part of the AuthContext map). Held as raw rows rather than a finished
  // map so the fetch depends only on leagueId/week: ctxMembers is a fresh
  // array on every LeagueContext realtime event, and having it in the fetch
  // deps re-queried the table on each one.
  const [ownershipRows, setOwnershipRows] = useState(null); // null = first load pending

  // Cached per league and week, so returning to the tab does not re-ask who
  // owns what. Ownership only moves when someone adds, bids or cuts, and
  // every one of those paths calls invalidateLeague, which sweeps this key.
  // Never persisted: another manager's add is invisible to that invalidation,
  // so this has to expire on its own within the session.
  //
  // `force` is for the add sheet's own callback, which runs immediately after
  // a write and must not be served the copy that write just invalidated in
  // another tab's memory.
  const loadOwnership = useCallback(async (force = false) => {
    if (!leagueId || !currentWeek) return;
    const cacheKey = `ownership:${leagueId}:week:${currentWeek}`;
    if (!force) {
      const cached = getCached(cacheKey);
      if (cached) {
        setOwnershipRows(cached);
        return;
      }
    }
    const { data } = await supabase
      .from("weekly_lineups")
      .select("member_id, starters, bench")
      .eq("league_id", leagueId)
      .eq("week", currentWeek);
    setOwnershipRows(data || []);
    setCached(cacheKey, data || [], IDLE_TTL_MS);
  }, [leagueId, currentWeek]);

  useEffect(() => { loadOwnership(); }, [loadOwnership]);

  // Schedule comes from `games`, never from team_season_stats.next_opponent.
  // next_opponent is written only by the CFBD lines cron, and only for the

  // This tab answers "should I pick this team up", so the matchup it shows is
  // the next game that can actually score: week 1 at the earliest, never the
  // week 0 opening weekend. The 12 teams playing on August 29 show their
  // September game here, because their August one earns a manager nothing.
  //
  // Week 0 is not hidden from the app, only from this question. The team
  // drawer's schedule strip lists it in full, labelled "Wk 0".
  const scheduleWeek = useMemo(() => {
    const wk = Number(seasonConfig?.currentWeek);
    return Number.isFinite(wk) && wk > FIRST_SCORING_WEEK ? wk : FIRST_SCORING_WEEK;
  }, [seasonConfig?.currentWeek]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The same rows the Scoreboard tab reads, from the same cache. This
      // tab always shows a week that is current or ahead, so it never asks
      // for the persisted treatment a finished week gets.
      const rows = await fetchWeekGames(scheduleWeek);
      if (cancelled) return;
      setGamesBySlug(buildGamesBySlug(rows));
    })();
    return () => { cancelled = true; };
  }, [scheduleWeek]);

  const ownership = useMemo(() => {
    const map = {};
    (ownershipRows || []).forEach((row) => {
      const member = ctxMembers.find((m) => m.id === row.member_id);
      const owner = member
        ? { memberId: member.id, teamName: member.team_name || "Unnamed team", isMe: member.id === currentMemberId }
        : null;
      [...(row.starters || []), ...(row.bench || [])].forEach((slug) => {
        if (slug) map[slug] = owner;
      });
    });
    return map;
  }, [ownershipRows, ctxMembers, currentMemberId]);

  // Everything below the filter bar stays hidden until ownership resolves.
  // Rendering earlier put every team in "Free agents" with no action button,
  // then reshuffled the list and popped the buttons in a moment later.
  const ownershipReady = ownershipRows !== null;

  const weekKey = `week${currentWeek}`;
  const myLineup = weeklyLineups[weekKey] || { starters: [], bench: [] };
  const myRosterSlugs = [...myLineup.starters, ...myLineup.bench].filter(Boolean);

  const conferences = useMemo(() => {
    const set = new Set();
    Object.values(allTeams || {}).forEach((t) => {
      if ((t.classification || "").toLowerCase() === "fbs" && t.conference) set.add(t.conference);
    });
    return ["All", ...Array.from(set).sort()];
  }, [allTeams]);

  // This team's line for the week being shown, as a number. Sorting needs the
  // value rather than the "+3.5"/"PICK" string the chips render.
  const teamSpread = useCallback((slug) => {
    const game = gamesBySlug[slug];
    if (!game || typeof game.homeSpread !== "number") return Number.POSITIVE_INFINITY;
    // Already this team's own line: buildGamesBySlug negates it for the away
    // entry when it builds the map. Consulting isHome here would swap the
    // away side back and sort every road team the wrong way round.
    return game.homeSpread;
  }, [gamesBySlug]);

  const browseTeams = useMemo(() => {
    let list = Object.values(allTeams || {}).filter(
      (t) => (t.classification || "").toLowerCase() === "fbs"
    );
    if (activeConference !== "All") list = list.filter((t) => t.conference === activeConference);
    if (watchlistOnly && watchlist.slugs) {
      list = list.filter((t) => watchlist.slugs.has(normalizeTeamName(t.school)));
    }
    if (searchQuery) {
      list = list.filter((t) => t.school.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return list;
  }, [allTeams, activeConference, searchQuery, watchlistOnly, watchlist.slugs]);

  // Shared by both sections so they cannot drift into ordering the same key
  // differently. Returns a comparator rather than a sorted list, since each
  // section sorts its own half.
  const comparatorFor = useCallback(
    (sortBy) => (a, b) => {
      const seasonA = a.currentSeason || {}, seasonB = b.currentSeason || {};
      if (sortBy === "points") return (seasonB.gamePoints || 0) - (seasonA.gamePoints || 0);
      if (sortBy === "record") {
        const [winsA] = parseRecord(seasonA.record);
        const [winsB] = parseRecord(seasonB.record);
        return winsB - winsA;
      }
      // Most favoured first, so the pickups most likely to score lead the
      // list. Negative is favoured, matching scoring.js. A bye or a game
      // whose line the CFBD cron has not written yet sorts to the bottom
      // rather than to the top, which a plain 0 default would do: no line is
      // not the same as a coin flip.
      if (sortBy === "spread") {
        return teamSpread(normalizeTeamName(a.school)) - teamSpread(normalizeTeamName(b.school));
      }
      return a.school.localeCompare(b.school);
    },
    [teamSpread]
  );

  const freeAgentTeams = useMemo(
    () => browseTeams
      .filter((t) => !ownership[normalizeTeamName(t.school)])
      .sort(comparatorFor(faSort)),
    [browseTeams, ownership, comparatorFor, faSort]
  );
  const ownedTeams = useMemo(
    () => browseTeams
      .filter((t) => ownership[normalizeTeamName(t.school)])
      .sort(comparatorFor(ownedSort)),
    [browseTeams, ownership, comparatorFor, ownedSort]
  );

  // Has anybody scored yet? Decides what the trending strip can rank on, and
  // what its cards can show. False for the whole preseason.
  const hasSeasonPoints = useMemo(
    () => Object.values(allTeams || {}).some((t) => (t.currentSeason?.gamePoints || 0) > 0),
    [allTeams]
  );

  // Trending carousel: always league-wide among free agents, independent of
  // the conference chip / search filters below it, same as ESPN's "Trending
  // Players" strip isn't scoped to the position filter under it.
  //
  // Ranking has to survive a season that has not started. Sorting on
  // gamePoints alone looked right but wasn't: every team is on 0 in the
  // preseason, sort is stable, so the strip silently degraded to the first
  // eight rows in whatever order Postgres returned them. The fallback is the
  // betting line on each team's next game, biggest favourite first, which is
  // the same signal backend/lineups.js uses to pick a fallback captain.
  // Alphabetical is the last resort, for before the lines land: arbitrary,
  // but at least deterministic and visibly so.
  // Game-wide pickups over the last week (migration 077). Feeds the ranking
  // and the "Popular pickup" tag; the count itself is never shown. A failure
  // (offline, or the function missing) leaves demand out of the ranking
  // rather than emptying the strip.
  const [recentAdds, setRecentAdds] = useState(() => getCached("trending_adds:7") || {});
  useEffect(() => {
    if (getCached("trending_adds:7")) return;
    let cancelled = false;
    supabase.rpc("trending_adds", { p_days: 7 }).then(({ data, error }) => {
      if (cancelled || error) return;
      const map = Object.fromEntries((data || []).map((r) => [r.team_id, r.adds]));
      setCached("trending_adds:7", map, IDLE_TTL_MS);
      setRecentAdds(map);
    });
    return () => { cancelled = true; };
  }, []);

  const popular = useMemo(() => popularPickups(recentAdds), [recentAdds]);

  const topFreeAgents = useMemo(() => {
    const pool = Object.values(allTeams || {})
      .filter((t) => (t.classification || "").toLowerCase() === "fbs")
      .filter((t) => !ownership[normalizeTeamName(t.school)]);
    return rankTrending({
      pool,
      slugOf: (t) => normalizeTeamName(t.school),
      gamesBySlug,
      adds: recentAdds,
      isLocked: (slug) => isTeamLocked(gamesBySlug[slug], slug).locked,
      hasSeasonPoints,
    });
  }, [allTeams, ownership, gamesBySlug, hasSeasonPoints, recentAdds]);

  // The matchup as parts rather than one string, so each surface can lay it
  // out for the width it has: a 158px trending card puts the kickoff and the
  // opponent on separate lines, a full-width row keeps them together, and
  // both lift the spread out into a chip. As one sentence it ran off the end
  // of every narrow surface, taking the spread with it.
  //
  // `short` picks the abbreviated opponent, which is only worth it on the
  // trending cards. Rows have the room for the real school name.
  const matchupParts = (slug, { short = false } = {}) => {
    const game = gamesBySlug[slug];
    if (!game) return { kind: "bye" };

    // buildGamesBySlug already swaps scores to this team's perspective, so
    // homeScore is this team's score regardless of where the game is played.
    const opponent = short
      ? shortOpponent(game.awayTeam, allTeams)
      : allTeams?.[game.awayTeam]?.school || game.awayTeam;
    const versus = `${game.isHome === false ? "@" : "vs"} ${opponent}`;

    if (game.gameComplete) {
      const won = game.homeScore > game.awayScore;
      return { kind: "final", versus, detail: `${won ? "W" : "L"} ${game.homeScore}-${game.awayScore}` };
    }
    if (isGameLive(game)) {
      return { kind: "live", versus, detail: `${game.homeScore}-${game.awayScore} live` };
    }

    // Roughly half of kickoff times are TBD until ~12 days out, and the CFBD
    // lines cron fills the spread in separately, so the two placeholders are
    // named apart rather than both reading "TBD".
    return {
      kind: "upcoming",
      versus,
      when: formatKickoff(game.date) || "Time TBD",
      spread: getGameSpreadDisplay(game, slug),
    };
  };

  const renderTeamRow = (team) => {
    const slug = normalizeTeamName(team.school);
    const owner = ownership[slug];
    const season = team.currentSeason || {};
    const matchup = matchupParts(slug);

    return (
      <div key={slug} className="flex items-center gap-2.5 pl-2 pr-3 py-2">
        {/* Action slot: add or bid, then the watchlist star. Fixed width
            whether or not the add button is there, so the stars and logos in
            owned and free-agent rows line up with each other. */}
        <div className="flex-shrink-0 flex items-center gap-1" style={{ width: ACTION_SLOT_WIDTH }}>
          <div className="flex items-center justify-center" style={{ width: 28 }}>
          {!owner && canAcquire && lockedForAdd(slug) && (
            <span aria-label={`${team.school}'s game has started`} title="Game has started" className="flex items-center justify-center" style={{ width: 28, height: 28 }}>
              <Lock size={14} color="var(--color-text-faint)" />
            </span>
          )}
          {!owner && canAcquire && !lockedForAdd(slug) && (
            <button
              onClick={() => setBidTarget(slug)}
              aria-label={`${instantAddOpen ? "Add" : "Bid on"} ${team.school}`}
              className="flex items-center justify-center rounded-full active:opacity-70"
              style={{ width: 28, height: 28, backgroundColor: "var(--color-brand)" }}
            >
              <Plus size={16} strokeWidth={3} color="var(--color-bg-card)" />
            </button>
          )}
          </div>
          {currentMemberId && !isDemo && (
            <button
              onClick={() => toggleWatch(slug)}
              disabled={watchlist.slugs === null}
              aria-pressed={watchlist.isWatched(slug)}
              aria-label={`${watchlist.isWatched(slug) ? "Remove" : "Add"} ${team.school} ${watchlist.isWatched(slug) ? "from" : "to"} your watchlist`}
              className="flex items-center justify-center rounded-full active:opacity-70 disabled:opacity-50"
              style={{ width: 28, height: 28 }}
            >
              <Star size={18} className={watchlist.isWatched(slug) ? "fill-amber-500 text-captain" : "text-ink-faint"} />
            </button>
          )}
        </div>

        <button
          onClick={() => setDrawerTeam(team.school)}
          className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
        >
          <TeamLogoImage teamName={team.school} primaryColor={team.colors?.primary} size={28} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--color-text-strong)" }}>
              <span className="truncate">{team.school}</span>
              <SpreadChip value={matchup.spread} />
            </div>
            {/* Opponent on its own line. Trailing the kickoff, it was the
                part truncated away on a phone, leaving "vs ..." on most rows. */}
            {matchup.kind !== "bye" && (
              <div className="text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>
                {matchup.versus}
              </div>
            )}
            <div className="text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>
              {matchup.kind === "bye"
                ? "Bye week"
                : matchup.kind === "upcoming"
                  ? matchup.when
                  : matchup.detail}
              {owner && (
                <span style={{ color: owner.isMe ? "var(--color-brand)" : "var(--color-text-muted)" }}>
                  {" · "}{owner.isMe ? "Your team" : owner.teamName}
                </span>
              )}
            </div>
          </div>
        </button>

        <div className="flex-shrink-0 text-center" style={{ width: 42 }}>
          <div className="text-sm font-bold" style={{ color: "var(--color-brand)" }}>{season.gamePoints || 0}</div>
        </div>
        <div className="flex-shrink-0 text-center" style={{ width: 42 }}>
          <div className="text-sm font-semibold" style={{ color: "var(--color-text-strong)" }}>{season.record || "0-0"}</div>
        </div>
      </div>
    );
  };

  // Column labels sit above each list so the two bare numbers on every row
  // are identifiable without a legend. The flex structure, gaps and padding
  // have to match renderTeamRow exactly or the labels drift off the numbers
  // they label. Change one, change both.
  //
  // That is also why this takes no full-bleed padding of its own: below sm
  // the card runs to the screen edge, so the rows measure their pl-2/pr-3
  // from that edge and the header has to measure from the same one.
  const listHeader = (label) => (
    <div className="flex items-center gap-2.5 pl-2 pr-3 mb-1.5">
      <div className="flex-shrink-0" style={{ width: ACTION_SLOT_WIDTH }} />
      <h4 className="flex-1 min-w-0 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
        {label}
      </h4>
      <div className="flex-shrink-0 text-center text-xs font-semibold" style={{ width: 42, color: "var(--color-text-muted)" }}>PTS</div>
      <div className="flex-shrink-0 text-center text-xs font-semibold" style={{ width: 42, color: "var(--color-text-muted)" }}>REC</div>
    </div>
  );

  const listCard = (children) => (
    <div
      className="sm:rounded-2xl border-y sm:border shadow-sm overflow-hidden divide-y"
      style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
    >
      {children}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg-page)" }}>
      {/* Full bleed below sm, as on the other three tabs. Text blocks and
          controls keep their own px-4 there; the trending row deliberately
          does not, so a card peeks off the right edge. */}
      <div className="max-w-4xl mx-auto sm:px-6 py-6 pb-24">
        {ownershipReady && topFreeAgents.length > 0 && (
          <div className="mb-6" data-tour="teams-trending">
            <h3 className="flex items-center gap-2 text-base font-bold uppercase tracking-wide mb-3 px-4 sm:px-1" style={{ color: "var(--color-text-strong)" }}>
              <TrendingUp size={20} strokeWidth={2.5} /> Trending teams
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 px-4 sm:px-0">
              {topFreeAgents.map((team) => {
                const slug = normalizeTeamName(team.school);
                const season = team.currentSeason || {};
                const matchup = matchupParts(slug, { short: true });
                const panel = trendingPanel(team);
                const onDark = panel.fg === PANEL_LIGHT_INK;
                // Translucent wash of the text colour: reads as a raised
                // well on any primary without picking a second colour.
                const well = onDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.07)";
                const soft = onDark ? "rgba(255,255,255,0.75)" : "rgba(28,28,30,0.65)";
                const scored = Object.entries(season.weeklyPoints || {})
                  .filter(([w, p]) => String(w) !== "0" && typeof p === "number")
                  .sort(([a], [b]) => Number(a) - Number(b));
                const lastThree = scored.slice(-3);
                const avg = calculateAverage(season.gamePoints || 0, scored.length);
                const stats = [
                  { label: "Avg", value: hasSeasonPoints ? avg : "—" },
                  { label: "ATS", value: season.atsRecord || "—" },
                  { label: "Rec", value: season.record || "0-0" },
                ];
                return (
                  <div
                    key={slug}
                    className="flex-shrink-0 rounded-2xl shadow-sm overflow-hidden flex flex-col"
                    style={{ width: 184, backgroundColor: panel.bg, color: panel.fg, borderTop: `4px solid ${panel.stripe}` }}
                  >
                    {/* The whole card in the school's colours. The helmet
                        sits in a white disc because /public/logos are
                        stickers on an opaque white square: on a coloured
                        ground the disc swallows the square instead of
                        framing it. */}
                    <button
                      onClick={() => setDrawerTeam(team.school)}
                      className="w-full flex items-center gap-2.5 px-3 pt-3 pb-2.5 text-left"
                      aria-label={`${team.school} details`}
                    >
                      <span
                        className="flex-shrink-0 rounded-full bg-surface-card overflow-hidden flex items-center justify-center"
                        style={{ width: 44, height: 44, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }}
                      >
                        <img src={teamLogoUrl(team.school)} alt="" style={{ width: 34, height: 34 }} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-extrabold leading-tight truncate">{team.school}</span>
                        <span className="block text-[11px] truncate" style={{ color: soft }}>
                          {[team.mascot, team.conference].filter(Boolean).join(" · ") || "N/A"}
                        </span>
                      </span>
                    </button>

                    <div className="grid grid-cols-3 mx-3 rounded-xl" style={{ backgroundColor: well }}>
                      {stats.map((st) => (
                        <div key={st.label} className="py-1.5 text-center">
                          <div className="text-[15px] font-extrabold leading-tight tabular-nums">{st.value}</div>
                          <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: soft }}>{st.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="px-3 pt-2.5 pb-3 flex flex-col flex-1 gap-2">
                      {/* Last weeks as a bar chart on a baseline, not
                          chips: the spread below is a chip, and two rows
                          of the same shape read as the same kind of
                          number. Scale is the scoring range, +13 to -8. */}
                      {lastThree.length > 0 && (
                        <div className="flex items-end gap-2">
                          <span className="font-bold uppercase tracking-wider text-[9px] pb-4" style={{ color: soft }}>
                            Last {lastThree.length}
                          </span>
                          <div className="flex-1 flex gap-1.5">
                            {lastThree.map(([w, p]) => {
                              const up = 24;
                              const down = 9;
                              const h = p === 0 ? 0 : Math.max(2, Math.round(p > 0 ? (p / 13) * up : (Math.abs(p) / 8) * down));
                              return (
                                <div key={w} className="flex-1 flex flex-col items-center">
                                  <div className="w-full flex items-end justify-center" style={{ height: up }}>
                                    {p > 0 && <div className="rounded-t-sm" style={{ width: 12, height: h, backgroundColor: panel.fg, opacity: 0.9 }} />}
                                  </div>
                                  <div className="w-full" style={{ height: 1, backgroundColor: soft }} />
                                  <div className="w-full flex justify-center" style={{ height: down }}>
                                    {p < 0 && <div className="rounded-b-sm" style={{ width: 12, height: h, backgroundColor: panel.fg, opacity: 0.5 }} />}
                                  </div>
                                  <span className="text-[10px] font-bold tabular-nums leading-none mt-1">
                                    {p > 0 ? `+${p}` : p}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="text-[11px] min-w-0">
                        {matchup.kind === "bye" ? (
                          <div className="font-semibold">Bye week</div>
                        ) : (
                          <>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold truncate">{matchup.versus}</span>
                              {matchup.spread && (
                                <span className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold tabular-nums" style={{ backgroundColor: well }}>
                                  {matchup.spread}
                                </span>
                              )}
                            </div>
                            <div className="truncate" style={{ color: soft }}>
                              {matchup.kind === "upcoming" ? matchup.when : matchup.detail}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Below the matchup, not under the name, so it
                          cannot push one card's stats and bars out of
                          line with the rest of the strip. */}
                      {popular.has(slug) && (
                        <span
                          className="mt-auto self-start inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide whitespace-nowrap"
                          style={{ backgroundColor: well }}
                        >
                          <TrendingUp size={11} strokeWidth={3} /> Popular pickup
                        </span>
                      )}
                      {canAcquire && lockedForAdd(slug) && (
                        <div
                          className="mt-auto w-full flex items-center justify-center gap-1 rounded-full text-xs font-bold"
                          style={{ minHeight: 34, backgroundColor: well, color: soft }}
                        >
                          <Lock size={12} /> Game Started
                        </div>
                      )}
                      {canAcquire && !lockedForAdd(slug) && (
                        <button
                          onClick={() => setBidTarget(slug)}
                          className="mt-auto w-full flex items-center justify-center gap-1 rounded-full text-xs font-bold active:opacity-80"
                          style={{ minHeight: 34, backgroundColor: panel.fg, color: panel.bg }}
                        >
                          <Plus size={14} strokeWidth={3} /> {instantAddOpen ? "Add" : "Bid"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Below Trending, which ignores these filters, and directly above
            the list they do filter. Pinned under the nav bar once scrolled to,
            so search is one flick away and stays in reach through the list.
            The offset is LeagueNavBar's height: 44 row + 12 padding + 1 border,
            plus the notch inset it pads by. */}
        <div
          data-tour="teams-search"
          className="sm:rounded-2xl border-y sm:border p-3 mb-3"
          style={{
            position: "sticky",
            top: "calc(57px + env(safe-area-inset-top))",
            zIndex: 30,
            backgroundColor: "var(--color-bg-card)",
            borderColor: "var(--color-border)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <div className="relative mb-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-muted)" }} />
            <input
              type="text"
              placeholder="Search teams"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-4 text-sm rounded-lg outline-none"
              style={{ backgroundColor: "var(--color-bg-subtle)", color: "var(--color-text-strong)", border: "none" }}
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {/* First in the row and pinned there, so the shortlist is one tap
                away without scrolling past sixteen conferences to find it.
                Hidden until the list has loaded and has something in it: an
                always-on chip that filters to nothing reads as a broken
                filter rather than an empty shortlist. */}
            {currentMemberId && watchlist.count > 0 && (
              <button
                onClick={() => setWatchlistOnly((v) => !v)}
                aria-pressed={watchlistOnly}
                className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
                style={watchlistOnly
                  ? { backgroundColor: "var(--color-captain-text)", color: "var(--color-bg-card)" }
                  : { backgroundColor: "var(--color-captain-fill)", color: "var(--color-captain-text)" }}
              >
                <Star size={11} className={watchlistOnly ? "fill-white" : "fill-amber-600"} />
                Watchlist {watchlist.count}
              </button>
            )}
            {conferences.map((c) => {
              const active = activeConference === c;
              return (
                <button
                  key={c}
                  onClick={() => setActiveConference(c)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
                  style={active ? { backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)" } : { backgroundColor: "var(--color-bg-subtle)", color: "var(--color-text-muted)" }}
                >
                  {c}
                </button>
              );
            })}
          </div>
          {watchError && (
            <p className="text-xs text-danger-text mt-1">{watchError}</p>
          )}
        </div>

        {!ownershipReady ? (
          listCard(
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 pl-2 pr-3 py-2 animate-pulse">
                <div style={{ width: ACTION_SLOT_WIDTH }} className="flex-shrink-0" />
                <div className="rounded-full flex-shrink-0" style={{ width: 28, height: 28, backgroundColor: "var(--color-border)" }} />
                <div className="flex-1 min-w-0">
                  <div className="h-3 rounded mb-1.5" style={{ width: "45%", backgroundColor: "var(--color-border)" }} />
                  <div className="h-2.5 rounded" style={{ width: "30%", backgroundColor: "var(--color-bg-subtle)" }} />
                </div>
              </div>
            ))
          )
        ) : (
          <>
            {/* The window's state, directly above the list it governs rather
                than across the top of the tab: it answers "can I take one of
                these", which is a question about the free agents, not about
                the search field. A strip, not a boxed card, so it reads as a
                section label instead of a warning. */}
            {noticeText && (
              <div className="flex items-center gap-2 px-4 sm:px-1 mb-3" data-tour="teams-notice">
                <span
                  className="rounded-full flex-shrink-0"
                  style={{ width: 8, height: 8, backgroundColor: canAcquire ? "var(--color-success)" : "var(--color-warning)" }}
                  aria-hidden="true"
                />
                <span className="text-[13px] font-semibold flex-1" style={{ color: "var(--color-text-strong)" }}>
                  {noticeText}
                  {/* Not in the demo: its strip says to sign up, which is not
                      a statement about free agency for a "?" to explain. */}
                  {!isDemo && <>{" "}<HelpMark step="teams-notice" label="How free agency works" /></>}
                </span>
                {noticeLinksToWaivers && (
                  <button
                    onClick={() => navigate(`/${leagueId}/lineup?open=waivers`)}
                    className="flex items-center gap-0.5 text-[13px] font-semibold flex-shrink-0"
                    style={{ color: "var(--color-brand)", minHeight: 32 }}
                  >
                    Your Claims <ChevronRight size={14} />
                  </button>
                )}
              </div>
            )}

            {freeAgentTeams.length > 0 && (
              <div className="mb-5" style={{ position: "relative" }}>
                {/* The list's heading and first few teams, not all of them:
                    see TourAnchor. */}
                <TourAnchor name="teams-list" height={200} />
                {listHeader("Free agents")}
                <SortSelect value={faSort} onChange={setFaSort} label="Sort free agents" />
                {listCard(freeAgentTeams.map(renderTeamRow))}
              </div>
            )}

            {ownedTeams.length > 0 && (
              <div className="mb-5">
                {listHeader("Owned")}
                <SortSelect value={ownedSort} onChange={setOwnedSort} label="Sort owned teams" />
                {listCard(ownedTeams.map(renderTeamRow))}
              </div>
            )}

            {browseTeams.length === 0 && (
              <div className="sm:rounded-2xl p-8 border-y sm:border shadow-sm text-center" style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}>
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>No teams match "{searchQuery}".</p>
              </div>
            )}
          </>
        )}
      </div>

      <TeamDrawer teamName={drawerTeam} onClose={() => setDrawerTeam(null)} onSelectTeam={setDrawerTeam} />

      {bidTarget && instantAddOpen && (
        <InstantAddSheet
          leagueId={leagueId}
          teamSlug={bidTarget}
          teamName={allTeams?.[bidTarget]?.school || bidTarget}
          myRoster={myRosterSlugs}
          lockedSlugs={new Set(myRosterSlugs.filter(lockedForAdd))}
          allTeams={allTeams}
          onClose={() => setBidTarget(null)}
          onAdded={() => loadOwnership(true)}
        />
      )}

      {bidTarget && !instantAddOpen && (
        <BidSheet
          leagueId={leagueId}
          memberId={currentMemberId}
          teamSlug={bidTarget}
          teamName={allTeams?.[bidTarget]?.school || bidTarget}
          myRoster={myRosterSlugs}
          allTeams={allTeams}
          onClose={() => setBidTarget(null)}
        />
      )}
    </div>
  );
}

export default TeamsTab;
